import { QueryStatus } from '@reduxjs/toolkit/query';
import { BaseQueryResult } from '@reduxjs/toolkit/query/react';
import React from 'react';

import { useDeleteFileMutation, useDownloadFileInfoQuery, useUploadFileMutation } from '@/api/v1';
import { FileInput } from '@/components/molecules/FileInput';
import { FileInterface } from '@/types/fileInterface';

interface Props {
  readonly name?: string;
  readonly path?: string;
  readonly value?: string;
}

export const FileFormFieldElement: React.FC<Props> = (props: Props): React.ReactElement => {
  const { name, path, value = '' } = props;

  const [file, setFile] = React.useState<FileInterface | null>(null);
  const [remove, removeMutation] = useDeleteFileMutation();
  const [upload, uploadMutation] = useUploadFileMutation();
  const [error, setError] = React.useState<string | null>(null);

  const {
    data: fetchedFileMetadata = null,
    status: fetchStatus,
    error: fetchError,
  } = useDownloadFileInfoQuery({ path: path, id: value }, { skip: !path || value === '' });

  if (!path) {
    throw new Error('path is required for file input');
  }

  React.useEffect((): void => {
    if (fetchError) {
      // TODO: do something about the error
    }
  }, [fetchError, path, remove, value]);

  React.useEffect((): void => {
    if (fetchStatus === QueryStatus.fulfilled) {
      if (fetchedFileMetadata) {
        const { name, mimeType, size } = fetchedFileMetadata;

        setFile({
          name: name,
          type: mimeType ?? 'application/octet-stream',
          size: size,
        });
      }
    } else if (fetchStatus === QueryStatus.rejected) {
      setFile(null);
    }
  }, [fetchStatus, fetchedFileMetadata, path]);

  const onFileChange = React.useCallback(
    (event: React.ChangeEvent): void => {
      const target = event.target as HTMLInputElement;
      const files = target.files ?? [];

      if (files[0]) {
        const file = files[0];

        setFile(file);
        upload({ path: path, file: file });
      }
    },
    [path, upload],
  );

  const handleRemove = React.useCallback((): void => {
    remove({ path: path, id: value });
    setFile(null);
  }, [path, remove, value]);

  const currentMutation = React.useMemo((): BaseQueryResult<any> | null => {
    if (uploadMutation.status !== QueryStatus.uninitialized) {
      if (removeMutation.status !== QueryStatus.uninitialized) {
        console.debug('both mutations are in a initialized state, this should never happen');
        return null;
      }

      return uploadMutation;
    } else if (removeMutation.status !== QueryStatus.uninitialized) {
      if (uploadMutation.status !== QueryStatus.uninitialized) {
        console.debug('both mutations are in a initialized state, this should never happen');
        return null;
      }
    }

    return removeMutation;
  }, [removeMutation, uploadMutation]);

  React.useEffect((): void => {
    if (currentMutation?.status === QueryStatus.pending) {
      setError(null);
    } else if (currentMutation?.status === QueryStatus.rejected) {
      setError(tryToExtractErrorFromMutation(currentMutation));
      currentMutation?.reset();
    } else if (currentMutation?.status === QueryStatus.fulfilled) {
      currentMutation?.reset();
    }
  }, [currentMutation]);

  const status = React.useMemo(
    (): QueryStatus => currentMutation?.status ?? QueryStatus.uninitialized,
    [currentMutation],
  );

  return (
    <>
      <input name={name} type="hidden" value={value} />

      <FileInput
        name={name}
        value={file}
        status={status}
        error={error}
        onChange={onFileChange}
        onRemove={handleRemove}
      />
    </>
  );
};

const tryToExtractErrorFromMutation = (mutationResult: any): string | null => {
  if (!mutationResult) {
    return 'Unknown error';
  }

  if (typeof mutationResult !== 'object') {
    return 'Unknown error';
  }

  if (!mutationResult.error) {
    return 'Unknown error';
  }

  if (!mutationResult.error.data) {
    return 'Unknown error';
  }

  return mutationResult.error.data.message ?? 'Unknown error';
};
